import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppNewVersionComponent } from '../../app-new-version/app-new-version.component';
import { ConfigitSettings } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';

export interface ProductTranslations {
    [key: string]: string;
}

@Injectable({
    providedIn: 'root',
})
export class AppService {
    public readonly localStorageKeyLanguage = 'cultureLang';
    public readonly localStorageKeySalesorg = 'salesOrg';

    constructor(private swUpdate: SwUpdate, private snackBar: MatSnackBar, private translate: TranslateService) {}

    public configuration$: BehaviorSubject<ConfigitSettings> = new BehaviorSubject<ConfigitSettings>(undefined);

    public onLanguageChanged$: BehaviorSubject<string> = new BehaviorSubject<string>(environment.app.defaultLanguage);

    public availableSalesOrgs$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    public availableLanguages$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    public salesOrg$: BehaviorSubject<string> = new BehaviorSubject<string>(environment.configit.plant);

    public appTitle$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public getSalesAreaId() {
        const salesArea = this.getSalesArea();
        if (!salesArea) {
            return '';
        }
        const { salesOrganization, distributionChannel, salesDivision } = salesArea;
        return `${salesOrganization}/${distributionChannel}/${salesDivision}`;
    }

    public getSalesOrg(): string {
        return this.salesOrg$.value;
    }

    public isNoPriceAdvantageCheckboxVisible() {
        const salesArea = this.getSalesArea();
        return salesArea?.noPriceAdvantage?.enabled;
    }

    private getSalesArea() {
        const salesOrg = this.getSalesOrg();
        const config = this.configuration$.value;
        if (config) {
            return config.allowedSalesAreas.find((salesArea) => salesArea.salesOrganization === salesOrg);
        }
        return null;
    }

    public setSalesOrg(salesOrg: string, persistent: boolean) {
        if (salesOrg && this.salesOrg$.value !== salesOrg) {
            if (persistent) {
                localStorage.setItem(this.localStorageKeySalesorg, salesOrg);
            }
            this.salesOrg$.next(salesOrg);
        }
    }

    public getSalesAreaName(): string {
        return this.getSalesArea()?.name;
    }

    public extractLanguage(languageAndCountry: string) {
        return languageAndCountry && languageAndCountry.includes('-')
            ? languageAndCountry.split('-')[0]
            : languageAndCountry;
    }

    public getLocalStorageSalesOrg(): string {
        return localStorage.getItem(this.localStorageKeySalesorg);
    }

    public getLocalStorageLanguage(): string {
        return localStorage.getItem(this.localStorageKeyLanguage);
    }

    public getLanguage(): string {
        return this.onLanguageChanged$.value;
    }

    public setLanguage(languageAndCountry: string, persistent: boolean) {
        if (languageAndCountry) {
            this.translate.use(languageAndCountry);
            this.onLanguageChanged$.next(languageAndCountry);
            if (persistent) {
                localStorage.setItem(this.localStorageKeyLanguage, languageAndCountry);
            }
        }
    }

    public getLanguageOnly() {
        return this.extractLanguage(this.getLanguage());
    }

    public getLocation() {
        const location = this.getLanguage().split('-')[1] || this.getLanguageOnly().toUpperCase();
        if (location === 'EN') {
            return 'GB';
        } else {
            return location;
        }
    }

    public getBrowserLanguage() {
        return navigator.language;
    }

    public chooseTranslation(productTranslations?: ProductTranslations) {
        if (productTranslations === undefined) {
            return '';
        }

        return (
            productTranslations[this.getLanguage()] ||
            productTranslations[this.getLanguageOnly()] ||
            productTranslations[this.getBrowserLanguage()] ||
            productTranslations['de-DE'] ||
            ''
        );
    }

    public checkNewVersion(): void {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.available.subscribe(() => {
                const message = this.translate.instant('GENERAL.NEW_VERSION.MESSAGE');
                const reloadBtn = this.translate.instant('GENERAL.NEW_VERSION.RELOAD_BTN');
                this.snackBar.openFromComponent(AppNewVersionComponent, {
                    data: {
                        message,
                        reloadBtn,
                    },
                    duration: 0,
                    panelClass: 'info',
                });
            });
        }
    }

    public get isAuthorizedToUseTemplates() {
        const salesArea = this.getSalesArea();
        return salesArea?.templates?.enabled;
    }

    public get isOptionalPositionsEnabled() {
        const salesArea = this.getSalesArea();
        return salesArea?.optionalPositions?.enabled;
    }
}
